import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../lib/api';

export default function AppointmentManagementModal({ onClose, onAppointmentsUpdated }) {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        fetchAppointments();
    }, []);

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await fetch(`${API_BASE}/api/admin/appointments`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch appointments");
            }

            const data = await response.json();
            setAppointments(data);
        } catch (error) {
            console.error("Error fetching appointments:", error);
            setError("Failed to load appointments");
        } finally {
            setLoading(false);
        }
    };

    const updateAppointmentStatus = async (appointmentId, newStatus) => {
        try {
            const response = await fetch(`${API_BASE}/api/admin/appointments/${appointmentId}/status`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });

            if (!response.ok) {
                throw new Error("Failed to update status");
            }

            // Refresh the list
            fetchAppointments();

            // Notify parent component to update unread count
            if (onAppointmentsUpdated) {
                onAppointmentsUpdated();
            }
        } catch (error) {
            console.error("Error updating status:", error);
            alert("Failed to update appointment status");
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING': return 'bg-yellow-100 text-yellow-800';
            case 'APPROVED': return 'bg-green-100 text-green-800';
            case 'REJECTED': return 'bg-red-100 text-red-800';
            case 'CANCELLED': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'PENDING': return '⏳';
            case 'APPROVED': return '✅';
            case 'REJECTED': return '❌';
            case 'CANCELLED': return '🚫';
            default: return '📅';
        }
    };

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 pt-20">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-2xl font-bold text-slate-900">Appointment Requests</h2>
                    <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
                </div>
                <div className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>
                    )}

                    {loading ? (
                        <div className="text-center py-8 text-slate-500">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400 mr-2"></div>
                            Loading appointments...
                        </div>
                    ) : appointments.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            <div className="text-4xl mb-4">📅</div>
                            <p className="text-lg font-medium">No appointments yet</p>
                            <p className="text-sm">Student bookings will appear here for approval</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {appointments.map((appointment) => (
                                <div key={appointment.id} className="border rounded-lg p-4 hover:bg-slate-50">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h4 className="font-medium text-slate-900">
                                                {appointment.user.name} - {appointment.type} Training
                                            </h4>
                                            <p className="text-sm text-slate-600">{appointment.user.email}</p>
                                        </div>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                                            {getStatusIcon(appointment.status)} {appointment.status}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                                        <div>
                                            <span className="text-sm font-medium text-slate-700">Date & Time:</span>
                                            <p className="text-sm text-slate-600">{formatDateTime(appointment.start)}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-slate-700">Duration:</span>
                                            <p className="text-sm text-slate-600">
                                                {Math.round((new Date(appointment.end) - new Date(appointment.start)) / 60000)} minutes
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-slate-700">Truck:</span>
                                            <p className="text-sm text-slate-600">{appointment.truck.displayName || appointment.truck.name}</p>
                                        </div>
                                    </div>

                                    {appointment.notes && (
                                        <div className="mb-3">
                                            <span className="text-sm font-medium text-slate-700">Notes:</span>
                                            <p className="text-sm text-slate-600">{appointment.notes}</p>
                                        </div>
                                    )}

                                    <div className="flex gap-2">
                                        {appointment.status === 'PENDING' && (
                                            <>
                                                <button
                                                    onClick={() => updateAppointmentStatus(appointment.id, 'APPROVED')}
                                                    className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 transition-colors"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    onClick={() => updateAppointmentStatus(appointment.id, 'REJECTED')}
                                                    className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"
                                                >
                                                    Reject
                                                </button>
                                            </>
                                        )}
                                        {appointment.status === 'APPROVED' && (
                                            <button
                                                onClick={() => updateAppointmentStatus(appointment.id, 'CANCELLED')}
                                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                        {appointment.status === 'REJECTED' && (
                                            <button
                                                onClick={() => updateAppointmentStatus(appointment.id, 'APPROVED')}
                                                className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200 transition-colors"
                                            >
                                                Approve
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
